import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  PublicApiChannel,
  PublicApiKey,
  PublicApiScope,
  PublicApiWebhook,
  PublicApiWebhookEvent,
} from '@/shared/api/public-api';
import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';
import { useNotifications } from '@/shared/ui/Notifications';

export const compactEmptyStateSx = {
  borderRadius: '24px',
  height: { xs: 180, md: 220 },
};

export const controlSx = {
  '& .MuiInputBase-root': {
    borderRadius: '12px',
  },
};

export const checkboxGroupSx = {
  columnGap: 2,
  rowGap: 0,
  '& .MuiFormControlLabel-root': {
    minHeight: 32,
    mr: 0,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: 15,
  },
};

const listRowSx = {
  border: '1px solid',
  borderColor: 'custom.grey6',
  borderRadius: '16px',
  px: { xs: 1.5, md: 2 },
  py: 1.25,
  bgcolor: 'background.paper',
};

const SCOPE_TRANSLATION_KEYS: Record<PublicApiScope, string> = {
  'channels:read': 'channelsRead',
  'contacts:read': 'contactsRead',
  'chats:read': 'chatsRead',
  'messages:read': 'messagesRead',
  'messages:write': 'messagesWrite',
  'webhooks:write': 'webhooksWrite',
  '*': 'fullAccess',
};

const EVENT_TRANSLATION_KEYS: Record<PublicApiWebhookEvent, string> = {
  'message.created': 'messageCreated',
  'message.updated': 'messageUpdated',
  'message.deleted': 'messageDeleted',
  'message.reaction.updated': 'messageReactionUpdated',
};

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function channelLabel(channel: PublicApiChannel) {
  return (
    channel.name || channel.phone_number || `${channel.protocol} ${channel.id}`
  );
}

export function scopeLabel(scope: PublicApiScope, t: TFunction) {
  return t(
    `integrations:publicApi.keys.scopes.${SCOPE_TRANSLATION_KEYS[scope]}`,
    {
      defaultValue: scope,
    }
  );
}

export function eventLabel(event: PublicApiWebhookEvent, t: TFunction) {
  return t(
    `integrations:publicApi.webhooks.events.${EVENT_TRANSLATION_KEYS[event]}`,
    {
      defaultValue: event,
    }
  );
}

function statusLabel(status: string, t: TFunction) {
  return t(`integrations:publicApi.statuses.${status}`, {
    defaultValue: status,
  });
}

function summaryLabel(
  count: number,
  singular: string,
  few: string,
  many: string
) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return singular;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export function TokenAlert({
  label,
  value,
  onClose,
}: {
  label: string;
  value: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();

  const copyToken = async () => {
    await navigator.clipboard.writeText(value);
    addNotification({
      type: 'success',
      title: t('integrations:publicApi.copied'),
    });
  };

  return (
    <Alert
      severity="info"
      onClose={onClose}
      action={
        <Button size="small" variant="text" onClick={copyToken}>
          {t('common:buttons.copy')}
        </Button>
      }
    >
      <Stack spacing={1}>
        <CustomTypography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </CustomTypography>
        <CustomTypography
          component="code"
          variant="caption"
          sx={{
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            display: 'block',
          }}
        >
          {value}
        </CustomTypography>
      </Stack>
    </Alert>
  );
}

export function PublicApiKeyRow({
  apiKey,
  t,
  disabledRotate,
  disabledRevoke,
  onCopyPrefix,
  onRotate,
  onRevoke,
}: {
  apiKey: PublicApiKey;
  t: TFunction;
  disabledRotate: boolean;
  disabledRevoke: boolean;
  onCopyPrefix: () => void;
  onRotate: () => void;
  onRevoke: () => void;
}) {
  const hasFullAccess = apiKey.scopes.includes('*');
  const scopes = hasFullAccess
    ? [scopeLabel('*', t)]
    : apiKey.scopes.map((scope) => scopeLabel(scope, t));
  const scopeSummary = hasFullAccess
    ? scopeLabel('*', t)
    : `${scopes.length} ${summaryLabel(
        scopes.length,
        t('integrations:publicApi.keys.rights.one'),
        t('integrations:publicApi.keys.rights.few'),
        t('integrations:publicApi.keys.rights.many')
      )}`;

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      spacing={1.5}
      sx={listRowSx}
    >
      <Stack spacing={0.5} sx={{ minWidth: { lg: 230 }, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <CustomTypography sx={{ fontWeight: 600 }}>
            {apiKey.name}
          </CustomTypography>
          <Chip
            label={statusLabel(apiKey.status, t)}
            size="small"
            color={apiKey.status === 'active' ? 'success' : 'default'}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <CustomTypography
            component="code"
            variant="caption"
            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
          >
            {apiKey.prefix}
          </CustomTypography>
          <Tooltip title={t('integrations:publicApi.keys.copyPrefix')}>
            <IconButton size="small" onClick={onCopyPrefix}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack spacing={0.5} sx={{ flex: 1.2 }}>
        <CustomTypography variant="caption" color="text.secondary">
          {scopeSummary}
        </CustomTypography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {scopes.map((scope) => (
            <Chip key={scope} label={scope} size="small" variant="outlined" />
          ))}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ minWidth: { lg: 300 } }}
      >
        <Box sx={{ flex: 1 }}>
          <CustomTypography variant="caption" color="text.secondary">
            {t('integrations:publicApi.keys.createdAt')}
          </CustomTypography>
          <CustomTypography variant="body2">
            {formatDate(apiKey.created_at)}
          </CustomTypography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <CustomTypography variant="caption" color="text.secondary">
            {t('integrations:publicApi.keys.lastUsed')}
          </CustomTypography>
          <CustomTypography variant="body2">
            {formatDate(apiKey.last_used_at)}
          </CustomTypography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        <Tooltip title={t('integrations:publicApi.keys.rotate')}>
          <span>
            <IconButton
              size="small"
              disabled={apiKey.status !== 'active' || disabledRotate}
              onClick={onRotate}
            >
              <AutorenewRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('integrations:publicApi.keys.revoke')}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={apiKey.status !== 'active' || disabledRevoke}
              onClick={onRevoke}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export function PublicApiWebhookRow({
  webhook,
  t,
  disabledDelete,
  onCopyUrl,
  onDelete,
}: {
  webhook: PublicApiWebhook;
  t: TFunction;
  disabledDelete: boolean;
  onCopyUrl: () => void;
  onDelete: () => void;
}) {
  const events = webhook.events.map((event) => eventLabel(event, t));

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      spacing={1.5}
      sx={listRowSx}
    >
      <Stack spacing={0.5} sx={{ minWidth: { lg: 360 }, flex: 1.4 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <CustomTypography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {webhook.url}
          </CustomTypography>
          <Tooltip title={t('integrations:publicApi.webhooks.copyUrl')}>
            <IconButton size="small" onClick={onCopyUrl}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
        <CustomTypography variant="caption" color="text.secondary">
          {t('integrations:publicApi.webhooks.updatedAt')}:{' '}
          {formatDate(webhook.updated_at)}
        </CustomTypography>
      </Stack>

      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Chip
            label={statusLabel(webhook.status, t)}
            size="small"
            color={webhook.status === 'active' ? 'success' : 'default'}
          />
          <CustomTypography variant="caption" color="text.secondary">
            {events.length}{' '}
            {summaryLabel(
              events.length,
              t('integrations:publicApi.webhooks.eventCount.one'),
              t('integrations:publicApi.webhooks.eventCount.few'),
              t('integrations:publicApi.webhooks.eventCount.many')
            )}
          </CustomTypography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {events.map((event) => (
            <Chip key={event} label={event} size="small" variant="outlined" />
          ))}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        <Tooltip title={t('integrations:publicApi.webhooks.delete')}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={disabledDelete}
              onClick={onDelete}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
